import * as React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { LineInfo } from './LineInfo';
import { Button } from './buttons/Button';
import { axios } from '../api';
import { Close } from '../assets/Close';
import { Customer, DiscountExclusiveType } from '../pages/types';
import { BOTTOM_TOAST, generateErrorMsg, generateFullName, getCountryFlag } from '../utils';

type OneTimeDiscountModalProps = {
  customer: Customer;
  onConfirm: (discountCode: string | null) => void;
  onCancel: () => void;
};

export const OneTimeDiscountModal = ({
  customer,
  onConfirm,
  onCancel,
}: OneTimeDiscountModalProps) => {
  const [selectedDiscountType, setSelectedDiscountType] = useState<DiscountExclusiveType>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    setIsSubmitting(true);
    if (selectedDiscountType === DiscountExclusiveType.CX_CUSTOM_CODE) {
      onConfirm(selectedDiscountType);
      setIsSubmitting(false);
      return;
    }
    axios
      .post(`discounts/generate_exclusive_discount`, {
        discount_type: selectedDiscountType,
        eligible_country: customer.default_country,
        currency: customer.default_currency,
      })
      .then((res) => {
        const toastMsg = `Discount created and applied to one time order.`;
        toast.success(toastMsg, BOTTOM_TOAST);
        onConfirm(res.data.discount_code);
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-6 min-w-[500px] relative border">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h1 className="font-bold text-left">Apply discount to one-time order</h1>
        <div className="space-y-0">
          <LineInfo
            title="Customer"
            value={`${getCountryFlag(customer.default_country)} ${generateFullName(
              customer.first_name,
              customer.last_name
            )} (${customer.email})`}
            className="mb-0"
          />
        </div>
        <hr className="my-4" />
        <h3 className="font-bold text-left mb-1">
          Select the discount you want to apply to this order:
        </h3>
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_10_PERCENT}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value as DiscountExclusiveType)}
            />
            <span>10% off</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_30_PERCENT}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value as DiscountExclusiveType)}
            />
            <span>30% off</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_REPROCESS_100_PERCENT}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value as DiscountExclusiveType)}
            />
            <span>100% off (reprocess)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="discountType"
              value={DiscountExclusiveType.CX_CUSTOM_CODE}
              className="mr-2"
              onChange={(e) => setSelectedDiscountType(e.target.value as DiscountExclusiveType)}
            />
            <span>Custom code</span>
          </label>
        </div>
        <div className="mt-[15px] w-full flex justify-center">
          <Button
            label="Apply Discount"
            variant="primary"
            isDisabled={!selectedDiscountType || isSubmitting}
            onClick={() => handleSubmit()}
          />
        </div>
      </div>
    </div>
  );
};
